import { isValid, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { Outlet, useParams } from "react-router-dom";

import { faCircleCheck, faDownload, faGear, faPenToSquare, faShare, faTrash } from "@fortawesome/pro-solid-svg-icons";

import {
	DropDownMenu,
	Icon,
	Breadcrumbs as PyBreadcrumbs,
	Tabs,
	Text,
	TextDate,
	toast,
} from "@performyard/py-components";
import ComponentLoader from "@performyard/py-components/src/components/atoms/ComponentLoader";

import Breadcrumbs from "../../../components/Breadcrumbs";

import ErrorPage from "../../../pages/Error/ErrorPage";

import {
	useCompensationCycleDetails,
	useCycleHasUsersWithoutCompensationData,
	useDownloadCompensationCSV,
	useMyDecisionDetails,
} from "../../../api/hooks";
import { useIsNewNavigation } from "../../../api/hooks/defs/navigation";
import { useCycleDetailsTabs } from "./useCycleDetailsTabs";

import { getJobFileDownload } from "../../../selectors/jobs";

import { rootCrumbOrDashboard } from "../../../utils/breadcrumbOrigin";

import BasePageTemplate from "../../../pageTemplates/BasePageTemplate";
import { useCompensationAccess } from "../useCompensationAccess";
import CycleDetailsModal from "./CycleDetailsModals/CycleDetailsModal";

const AsyncJobDownloadByIdComponent = ({ jobId }: { jobId: string }): null => {
	const file = useSelector((state) => getJobFileDownload(state, jobId));
	const prevFileRef = useRef(file);

	useEffect(() => {
		if (file && file !== prevFileRef.current) {
			window.open(file, "_blank");
		}
		prevFileRef.current = file;
	}, [file]);

	return null;
};

type ModalTypes = "edit" | "delete" | "complete" | "reopen";
const CycleDetailsLayout: React.FC = () => {
	const { cycleId } = useParams<{ cycleId: string }>();
	const isNewNav = useIsNewNavigation();
	const [cycleModalOpen, setCycleModalOpen] = useState<ModalTypes | "closed">("closed");
	const {
		userID,
		isLoading: isAccessLoading,
		compensationEnabled,
		canMakeDecisions,
		canCreateCycle: hasCreateCyclePermission,
		canViewCompensation: userCanViewCompensation,
		canLoadMyDecisions,
		canDeleteCycle,
	} = useCompensationAccess(cycleId);
	const {
		data: cycleDetails,
		isLoading: isLoadingCycleDetails,
		isError: isErrorLoadingCycle,
	} = useCompensationCycleDetails(cycleId ?? "", {
		enabled: cycleId !== "",
	});
	const { data: cycleHasUsersWithoutCompensationData, isLoading: isLoadingUsersWithoutCompData } =
		useCycleHasUsersWithoutCompensationData(cycleId || "");
	const { data: myDecisionsData, isLoading: isLoadingMyDecisionsData } = useMyDecisionDetails(
		userID ?? "",
		cycleId ?? "",
		{
			enabled: canLoadMyDecisions,
		}
	);
	const [csvJobId, setCsvJobId] = useState<string | null>(null);
	const { mutate: downloadCSV } = useDownloadCompensationCSV(cycleId || "", {
		onSuccess: (data) => setCsvJobId(data.jobID),
	});

	const hasDecisionsToMake =
		!isLoadingMyDecisionsData && (myDecisionsData?.directReportsToMakeDecisionsFor?.results?.length ?? 0) > 0;

	const hasMyDecisionsTab =
		cycleDetails?.status === "active" && cycleDetails?.displayStatus !== "Scheduled" && hasDecisionsToMake;
	const cycleIsDeleted = cycleDetails?.deletedAt;

	const { activeTab, handleTabChange, tabItems } = useCycleDetailsTabs({
		cycleId,
		isAccessLoading,
		compensationEnabled,
		userCanViewCompensation,
		canLoadMyDecisions,
		hasCreateCyclePermission,
		canMakeDecisions,
		isLoadingMyDecisionsData,
		isLoadingCycleDetails,
		cycleDetails,
		hasMyDecisionsTab: Boolean(hasMyDecisionsTab),
	});

	const hasUsersWithoutCompData =
		!isLoadingUsersWithoutCompData && cycleHasUsersWithoutCompensationData?.cycleHasUsersMissingCompData === true;

	useEffect(() => {
		if (hasUsersWithoutCompData) {
			toast.error("Employee(s) missing compensation data");
		}
	}, [hasUsersWithoutCompData]);

	// Fast permission checks first — don't wait for slow cycle detail fetches
	if (isAccessLoading) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (!userCanViewCompensation) {
		return <ErrorPage code={403} />;
	}

	// Data-dependent loading — only reached after permissions are confirmed
	if (
		(canLoadMyDecisions && !cycleDetails && !isErrorLoadingCycle) ||
		(canLoadMyDecisions && isLoadingMyDecisionsData) ||
		isLoadingCycleDetails ||
		isLoadingUsersWithoutCompData
	) {
		return (
			<div className="flex h-[calc(100vh-104px)] flex-col items-center justify-center">
				<ComponentLoader />
			</div>
		);
	}

	if (isErrorLoadingCycle) {
		return <ErrorPage code={404} />;
	}
	if (cycleIsDeleted) {
		return <ErrorPage code={404} />;
	}

	if (!hasCreateCyclePermission && cycleDetails?.displayStatus === "Scheduled") {
		return <ErrorPage code={403} />;
	}

	if (hasUsersWithoutCompData) {
		return <ErrorPage code={500} />;
	}

	const tz =
		typeof cycleDetails?.timezone === "string" && cycleDetails.timezone.length > 0 ? cycleDetails.timezone : "UTC";
	const dueDateParsed = cycleDetails?.dueDate ? parseISO(cycleDetails.dueDate) : null;
	const cycleCompletedBeforeStartDate =
		cycleDetails.status === "completed" &&
		formatInTimeZone(parseISO(cycleDetails.startDate), tz, "yyyy-MM-dd") >
			formatInTimeZone(new Date(), tz, "yyyy-MM-dd");

	const optionsDropDownCycleOverview = [
		{
			key: "edit",
			label: "Edit",
			onClick: () => setCycleModalOpen("edit"),
			right: <Icon icon={faPenToSquare} size={16} color="neutral-7" />,
		},
		...(cycleDetails?.displayStatus !== "Scheduled" && !cycleCompletedBeforeStartDate
			? [
					{
						key: "download-results-csv",
						label: "Download Results CSV",
						onClick: () => downloadCSV({}),
						right: <Icon icon={faDownload} size={16} color="neutral-7" />,
					},
				]
			: []),
		...(cycleDetails?.status === "active"
			? [
					{
						key: "complete",
						label: "Complete Cycle",
						onClick: () => setCycleModalOpen("complete"),
						right: <Icon icon={faCircleCheck} size={16} color="neutral-7" />,
					},
				]
			: []),
		...(cycleDetails?.status === "completed"
			? [
					{
						key: "reopen",
						label: "Reopen Cycle",
						onClick: () => setCycleModalOpen("reopen"),
						right: <Icon icon={faShare} size={16} color="neutral-7" />,
					},
				]
			: []),
		...(canDeleteCycle
			? [
					{
						key: "delete",
						label: "Delete Cycle",
						onClick: () => setCycleModalOpen("delete"),
						right: <Icon icon={faTrash} size={16} color="neutral-7" />,
					},
				]
			: []),
	];

	const closeModal = () => setCycleModalOpen("closed");
	const modalShouldBeOpen =
		Boolean(cycleDetails) &&
		(cycleModalOpen === "complete" ||
			cycleModalOpen === "delete" ||
			cycleModalOpen === "edit" ||
			cycleModalOpen === "reopen");
	return (
		<BasePageTemplate title="Compensation Cycle">
			{csvJobId && <AsyncJobDownloadByIdComponent jobId={csvJobId} />}
			<div>
				{isNewNav ? (
					<PyBreadcrumbs
						items={[
							rootCrumbOrDashboard({
								pageId: "compensation",
								name: "Compensation",
								href: "/compensation",
								forceHardReload: true,
							}),
							{
								pageId: cycleId || "",
								name: String(cycleDetails?.name ?? ""),
								href: `/compensation/cycles/${cycleId}`,
							},
						]}
					/>
				) : (
					<Breadcrumbs
						pages={[
							{
								pageID: "compensation-cycles",
								label: "Compensation Management",
								url: "/compensation",
							},
							{
								pageID: cycleId || "",
								label: String(cycleDetails?.name ?? ""),
								url: `/compensation/cycles/${cycleId}`,
							},
						]}
						className={"breadcrumbs"}
						forceHardReload
					/>
				)}
			</div>
			<div className="flex flex-col gap-6 pt-6">
				<div>
					<div className="flex w-full items-center justify-between gap-4">
						<div className="min-w-0 flex-1">
							<Text size="heading-5" weight="semi-bold" overrideElement="h1">
								{String(cycleDetails?.name ?? "")}
							</Text>
						</div>
						{activeTab === "cycle-overview" ? (
							<div className="shrink-0">
								<DropDownMenu
									bordered
									options={optionsDropDownCycleOverview}
									placement="bottom-end"
									minWidth={223}
									className="flex h-[32px] w-[180px] shrink-0 items-center justify-between rounded-[4px] bg-[var(--Neutral-0,#FFF)] px-2"
								>
									<Text size="paragraph-3" weight="regular" dangerouslySetClassName="text-left">
										Options
									</Text>
									<Icon icon={faGear} size={16} color="neutral-7"></Icon>
								</DropDownMenu>
							</div>
						) : null}
					</div>

					<div className="flex items-center gap-1">
						<Text size="paragraph-3" weight="semi-bold" color="neutral-7">
							Due Date:
						</Text>
						{dueDateParsed && isValid(dueDateParsed) ? (
							<TextDate date={dueDateParsed} hasTime compactTime dateSize="medium" color="neutral-7" />
						) : null}
					</div>
					{tabItems.length > 0 ? (
						<div className="mt-6">
							<Tabs
								size="large"
								value={activeTab}
								color={"neutral"}
								onValueChange={handleTabChange}
								tabs={[...tabItems]}
							/>
						</div>
					) : null}
					<Outlet />
				</div>
			</div>
			{modalShouldBeOpen && (
				<CycleDetailsModal
					cycleId={cycleId || ""}
					cycleDetails={cycleDetails}
					cycleModalOpen={cycleModalOpen}
					closeModal={closeModal}
				/>
			)}
		</BasePageTemplate>
	);
};

export default CycleDetailsLayout;
